import { useMemo } from 'react';
import {
  MidInspectionAttachment,
  MidInspectionInput,
  MidInspectionItem,
  parseMidQty,
  validateMidInputStock,
} from '../../types/midInspection';
import {
  CompanyInventoryItem,
  formatInventoryQty,
  inventoryKindLabel,
} from '../../types/companyInventory';
import { MID_PROCESS_REQUIREMENT } from './midFormDefaults';
import InspectionRemarkFiles from './InspectionRemarkFiles';
import { formatNumberInput } from '../../utils/numberFormat';
import './InspectionFormSheet.css';
import './ProductionDailyReportPanel.css';

export interface MidSheetValues {
  inventoryId: number | null;
  itemKind: string;
  itemName: string;
  specText: string;
  authorName: string;
  reviewerName?: string;
  approverName?: string;
  lotNo: string;
  lotQty: string;
  inspectorName: string;
  inspectionDate: string;
  appearanceRemark: string;
  appearanceJudgment: string;
  electricalRemark: string;
  electricalJudgment: string;
  agingRemark: string;
  agingJudgment: string;
  overallJudgment: string;
  remark: string;
  items: MidInspectionItem[];
  inputs: MidInspectionInput[];
}

interface Props {
  values: MidSheetValues;
  editable?: boolean;
  attachmentsEditable?: boolean;
  inventoryOptions?: CompanyInventoryItem[];
  remarkAttachments?: MidInspectionAttachment[];
  pendingRemarkFiles?: File[];
  onChange?: (patch: Partial<MidSheetValues>) => void;
  onItemChange?: (index: number, patch: Partial<MidInspectionItem>) => void;
  onAddItem?: () => void;
  onRemoveItem?: (index: number) => void;
  onAddInput?: () => void;
  onInputChange?: (index: number, patch: Partial<MidInspectionInput>) => void;
  onRemoveInput?: (index: number) => void;
  onOpenModelPicker?: () => void;
  onOpenInputInventoryPicker?: (index: number) => void;
  onOpenInputLotPicker?: (index: number) => void;
  lotNoHint?: string;
  onPickRemarkFiles?: (files: File[]) => void;
  onRemovePendingRemarkFile?: (index: number) => void;
  onDownloadAttachment?: (file: MidInspectionAttachment) => void;
  onDeleteAttachment?: (file: MidInspectionAttachment) => void;
}

function inventoryPickSummary(
  itemName: string,
  specText?: string | null,
  itemKind?: string | null
) {
  const name = itemName?.trim();
  if (!name) return '';
  const kind = itemKind ? `[${inventoryKindLabel(itemKind)}] ` : '';
  return `${kind}${name}${specText?.trim() ? ` · ${specText.trim()}` : ''}`;
}

function MidInspectionSheet({
  values,
  editable = false,
  attachmentsEditable = editable,
  inventoryOptions = [],
  remarkAttachments = [],
  pendingRemarkFiles = [],
  onChange,
  onItemChange,
  onAddItem,
  onRemoveItem,
  onAddInput,
  onInputChange,
  onRemoveInput,
  onOpenModelPicker,
  onOpenInputInventoryPicker,
  onOpenInputLotPicker,
  onPickRemarkFiles,
  onRemovePendingRemarkFile,
  onDownloadAttachment,
  onDeleteAttachment,
  lotNoHint,
}: Props) {
  const field = (key: keyof MidSheetValues, value: string) =>
    onChange?.({ [key]: value } as Partial<MidSheetValues>);

  const modelSummary = inventoryPickSummary(values.itemName, values.specText, values.itemKind);

  const stockByInventoryId = useMemo(() => {
    const map = new Map<number, number>();
    for (const item of inventoryOptions) {
      map.set(item.inventoryId, Number(item.stockQty ?? 0));
    }
    for (const input of values.inputs) {
      if (!map.has(input.inventoryId) && input.stockQty != null && input.stockQty !== '') {
        map.set(input.inventoryId, Number(input.stockQty));
      }
    }
    return map;
  }, [inventoryOptions, values.inputs]);

  const inputStockError = useMemo(() => {
    if (!editable || values.inputs.length === 0) return null;
    return validateMidInputStock(values.inputs, stockByInventoryId);
  }, [editable, values.inputs, stockByInventoryId]);

  const requestedByInventory = useMemo(() => {
    const map = new Map<number, number>();
    for (const input of values.inputs) {
      const qty = parseMidQty(input.qty);
      if (qty == null || qty <= 0) continue;
      map.set(input.inventoryId, (map.get(input.inventoryId) ?? 0) + qty);
    }
    return map;
  }, [values.inputs]);

  return (
    <div className="ifs-sheet">
      <div className="ifs-sheet__top">
        <div>
          <h4 className="ifs-sheet__title">중간검사성적서</h4>
        </div>
        <div className="ifs-sheet__stamps">
          {[
            ['작성', values.authorName],
            ['검토', values.reviewerName],
            ['승인', values.approverName],
          ].map(([label, value]) => (
            <div key={label}>
              <strong>{label}</strong>
              <span
                style={{
                  color: value ? '#1f2a37' : '#98a2b3',
                  fontWeight: value ? 600 : 400,
                }}
              >
                {value || '-'}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="ifs-sheet__field-group">
        <h5 className="ifs-sheet__field-group-title">검사 대상 (결과 모델)</h5>
        <div className="ifs-sheet__fields is-cols-4">
          <label className="ifs-sheet__item-pick">
            모델명 (재고관리)
            {editable ? (
              <div className="ifs-sheet__item-row">
                <button
                  type="button"
                  className={`pdr-pick-btn${modelSummary ? ' has-value' : ''}`}
                  onClick={() => onOpenModelPicker?.()}
                >
                  {modelSummary || '모델 선택'}
                </button>
              </div>
            ) : (
              <input
                value={
                  values.itemName
                    ? `${values.itemName}${values.specText ? ` · ${values.specText}` : ''}`
                    : '-'
                }
                readOnly
              />
            )}
          </label>
          <label>
            생산 LOT NO.
            <input
              value={values.lotNo}
              readOnly
              placeholder={editable && !values.lotNo ? '저장 시 자동 발급' : undefined}
            />
          </label>
          <label>
            <span className="ifs-field-label">
              생산 LOT 수량
              {editable ? <span className="ifs-field-required" aria-hidden> *</span> : null}
            </span>
            <input
              value={values.lotQty}
              readOnly={!editable}
              required={editable}
              onChange={(e) => field('lotQty', e.target.value)}
            />
          </label>
          <label>
            검사 일자
            <input
              type="date"
              value={values.inspectionDate}
              readOnly={!editable}
              onChange={(e) => field('inspectionDate', e.target.value)}
            />
          </label>
          <label>
            검사자
            <input
              value={values.inspectorName}
              readOnly={!editable}
              onChange={(e) => field('inspectorName', e.target.value)}
            />
          </label>
        </div>
        {editable && !values.lotNo && lotNoHint ? (
          <p className="ifs-field-hint" style={{ marginTop: 8 }}>
            {lotNoHint}
          </p>
        ) : null}
      </div>

      <section className="ifs-sheet__field-group mid-input-section">
        <div className="pdr-run-card__materials-head" style={{ marginBottom: 10 }}>
          <div>
            <h5 className="ifs-sheet__field-group-title" style={{ margin: 0 }}>
              투입 원자재 / 부품
            </h5>
            <p style={{ margin: '4px 0 0', fontSize: 11, color: '#98a2b3' }}>
              재고관리 품목을 선택하고, Lot은 직접 입력하거나 인수검사에서 찾을 수 있습니다.
              승인 시 보유 재고가 차감됩니다.
            </p>
          </div>
          {editable ? (
            <button type="button" className="ifs-req__add" onClick={() => onAddInput?.()}>
              + 투입 추가
            </button>
          ) : null}
        </div>

        <div className="pdr-mat-scroll">
          <table className="pdr-mat-table mid-input-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>No</th>
                <th>품목</th>
                <th className="is-lot">Lot No.</th>
                <th style={{ width: 88 }}>보유재고</th>
                <th className="is-qty">투입수량</th>
                {editable ? <th className="is-actions" /> : null}
              </tr>
            </thead>
            <tbody>
              {values.inputs.length === 0 ? (
                <tr>
                  <td
                    colSpan={editable ? 6 : 5}
                    style={{ textAlign: 'center', color: '#98a2b3', padding: '16px 8px' }}
                  >
                    투입 원자재가 없습니다.
                    {editable ? ' 「투입 추가」로 재고 품목을 선택하세요.' : ''}
                  </td>
                </tr>
              ) : (
                values.inputs.map((input, index) => {
                  const stock = stockByInventoryId.get(input.inventoryId) ?? 0;
                  const requested = requestedByInventory.get(input.inventoryId) ?? 0;
                  const overStock = editable && requested > stock;
                  const inputSummary = inventoryPickSummary(
                    input.itemName || '',
                    input.specText,
                    input.itemKind
                  );
                  return (
                    <tr key={input.inputId ?? `mid-input-${index}`}>
                      <td style={{ color: '#98a2b3' }}>{index + 1}</td>
                      <td>
                        {editable ? (
                          <button
                            type="button"
                            className={`pdr-pick-btn${inputSummary ? ' has-value' : ''}`}
                            onClick={() => onOpenInputInventoryPicker?.(index)}
                          >
                            {inputSummary || '품목 선택'}
                          </button>
                        ) : (
                          <span style={{ fontWeight: 600 }}>
                            [{inventoryKindLabel(input.itemKind)}] {input.itemName || '-'}
                            {input.specText ? ` · ${input.specText}` : ''}
                          </span>
                        )}
                      </td>
                      <td className="is-lot">
                        {editable ? (
                          <div className="pdr-mat-lot">
                            <input
                              value={input.lotNo ?? ''}
                              readOnly
                              placeholder="Lot 찾기"
                              maxLength={100}
                            />
                            <button
                              type="button"
                              className="pdr-mat-lot__btn"
                              disabled={!input.inventoryId}
                              title={
                                input.inventoryId
                                  ? '이 품목의 인수검사 Lot에서 선택'
                                  : '품목을 먼저 선택하세요'
                              }
                              onClick={() => onOpenInputLotPicker?.(index)}
                            >
                              찾기
                            </button>
                          </div>
                        ) : (
                          input.lotNo || '-'
                        )}
                      </td>
                      <td>{formatInventoryQty(stock)}</td>
                      <td className="is-qty">
                        {editable ? (
                          <input
                            className={overStock ? 'is-overstock' : undefined}
                            inputMode="decimal"
                            value={input.qty ?? ''}
                            onChange={(e) =>
                              onInputChange?.(index, { qty: formatNumberInput(e.target.value) })
                            }
                            placeholder="수량"
                          />
                        ) : (
                          input.qty || '-'
                        )}
                      </td>
                      {editable ? (
                        <td className="is-actions">
                          <button
                            type="button"
                            className="ncr-btn is-danger"
                            onClick={() => onRemoveInput?.(index)}
                          >
                            삭제
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {inputStockError ? <p className="ifs-sheet__inline-error">{inputStockError}</p> : null}
      </section>

      <section className="ifs-sheet__field-group ifs-req">
        <div className="ifs-req__head">
          <div>
            <h5 className="ifs-sheet__field-group-title" style={{ margin: 0 }}>
              {MID_PROCESS_REQUIREMENT}
            </h5>
            <p className="ifs-req__hint">
              {values.items.length > 0
                ? `${values.items.length}개 항목`
                : editable
                  ? '검사항목을 추가해 주세요'
                  : '등록된 항목 없음'}
            </p>
          </div>
          {editable ? (
            <button type="button" className="ifs-req__add" onClick={() => onAddItem?.()}>
              + 행 추가
            </button>
          ) : null}
        </div>

        {values.items.length === 0 ? (
          <div className="ifs-req__empty">
            <strong>요구사항이 없습니다</strong>
            <span>{editable ? '행 추가로 검사항목·기준·방법을 입력하세요.' : ''}</span>
          </div>
        ) : (
          <div className="ifs-req__list">
            {values.items.map((item, index) => {
              const judgment = (item.judgment ?? '').trim();
              const judgmentClass =
                judgment === '적합'
                  ? 'is-pass'
                  : judgment === '부적합'
                    ? 'is-fail'
                    : 'is-pending';
              return (
                <article key={item.itemId ?? `req-${index}`} className="ifs-req__card">
                  <header className="ifs-req__card-top">
                    <span className="ifs-req__no">{index + 1}</span>
                    <div className="ifs-req__title-wrap">
                      {editable ? (
                        <textarea
                          className="ifs-req__title-input"
                          value={item.itemName ?? ''}
                          onChange={(e) => onItemChange?.(index, { itemName: e.target.value })}
                          placeholder="검사항목명"
                          rows={1}
                        />
                      ) : (
                        <strong className="ifs-req__title-text">{item.itemName || '-'}</strong>
                      )}
                    </div>
                    <div className={`ifs-req__judgment ${judgmentClass}`}>
                      {editable ? (
                        <select
                          value={item.judgment ?? ''}
                          onChange={(e) => onItemChange?.(index, { judgment: e.target.value })}
                        >
                          <option value="">판정</option>
                          <option value="적합">적합</option>
                          <option value="부적합">부적합</option>
                        </select>
                      ) : (
                        <span>{judgment || '-'}</span>
                      )}
                    </div>
                    {editable ? (
                      <button
                        type="button"
                        className="ifs-req__remove"
                        onClick={() => onRemoveItem?.(index)}
                        title="삭제"
                        aria-label="요구사항 삭제"
                      >
                        삭제
                      </button>
                    ) : null}
                  </header>

                  <div className="ifs-req__body">
                    <label className="ifs-req__field">
                      <span>검사기준</span>
                      {editable ? (
                        <textarea
                          value={item.standardText ?? ''}
                          onChange={(e) =>
                            onItemChange?.(index, { standardText: e.target.value })
                          }
                          placeholder="합격 기준을 입력하세요"
                          rows={3}
                        />
                      ) : (
                        <p className="ifs-req__readonly">{item.standardText || '-'}</p>
                      )}
                    </label>
                    <label className="ifs-req__field">
                      <span>검사방법 및 조건</span>
                      {editable ? (
                        <textarea
                          value={item.methodText ?? ''}
                          onChange={(e) => onItemChange?.(index, { methodText: e.target.value })}
                          placeholder="검사 방법·조건을 입력하세요"
                          rows={3}
                        />
                      ) : (
                        <p className="ifs-req__readonly">{item.methodText || '-'}</p>
                      )}
                    </label>
                  </div>

                  <div className="ifs-req__samples">
                    <span className="ifs-req__samples-label">시료</span>
                    <div className="ifs-req__sample-grid">
                      {(['sample1', 'sample2', 'sample3', 'sample4', 'sample5'] as const).map(
                        (key, sampleIndex) => (
                          <label key={key} className="ifs-req__sample">
                            <span>X{sampleIndex + 1}</span>
                            <input
                              value={item[key] ?? ''}
                              readOnly={!editable}
                              onChange={(e) =>
                                onItemChange?.(index, { [key]: e.target.value })
                              }
                              placeholder="-"
                            />
                          </label>
                        )
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <div
        className={`ifs-overall${
          values.overallJudgment === '적합'
            ? ' is-pass'
            : values.overallJudgment === '부적합'
              ? ' is-fail'
              : ''
        }`}
      >
        <div className="ifs-overall__head">
          <div>
            <h5 className="ifs-sheet__field-group-title" style={{ margin: 0 }}>
              종합판정
              {editable ? <span className="ifs-field-required" aria-hidden> *</span> : null}
            </h5>
            <p className="ifs-overall__hint">
              검사 항목을 모두 확인한 뒤 최종 판정하세요
            </p>
          </div>
          {!editable && values.overallJudgment ? (
            <span
              className={`ifs-overall__badge${
                values.overallJudgment === '적합'
                  ? ' is-pass'
                  : values.overallJudgment === '부적합'
                    ? ' is-fail'
                    : ''
              }`}
            >
              {values.overallJudgment}
            </span>
          ) : null}
        </div>
        {editable ? (
          <div className="ifs-overall__choices" role="group" aria-label="종합판정">
            <button
              type="button"
              className={`ifs-overall__choice is-pass${
                values.overallJudgment === '적합' ? ' is-selected' : ''
              }`}
              onClick={() => field('overallJudgment', '적합')}
            >
              <strong>적합</strong>
              <span>합격</span>
            </button>
            <button
              type="button"
              className={`ifs-overall__choice is-fail${
                values.overallJudgment === '부적합' ? ' is-selected' : ''
              }`}
              onClick={() => field('overallJudgment', '부적합')}
            >
              <strong>부적합</strong>
              <span>불합격 · NCR 검토</span>
            </button>
          </div>
        ) : !values.overallJudgment ? (
          <p className="ifs-overall__empty">판정 없음</p>
        ) : null}
      </div>

      <div className="ifs-sheet__remark">
        <div className="ifs-sheet__toolbar">
          <strong>비고</strong>
          {attachmentsEditable ? (
            <label className="ifs-sheet__file-btn is-soft">
              파일 첨부
              <input
                type="file"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  if (files.length) onPickRemarkFiles?.(files);
                  e.target.value = '';
                }}
              />
            </label>
          ) : null}
        </div>
        {editable ? (
          <textarea
            value={values.remark}
            onChange={(e) => field('remark', e.target.value)}
            rows={3}
            placeholder="공인기관 성적서 대체 시 비고와 함께 첨부하세요."
            style={{ marginBottom: 8 }}
          />
        ) : (
          <p style={{ margin: '0 0 8px', fontSize: 13, whiteSpace: 'pre-wrap' }}>
            {values.remark || '-'}
          </p>
        )}
        <InspectionRemarkFiles
          attachments={remarkAttachments}
          pendingFiles={pendingRemarkFiles}
          editable={attachmentsEditable}
          emptyText="공인기관 성적서 등 비고 첨부파일이 없습니다."
          onDownload={onDownloadAttachment}
          onDeleteSaved={onDeleteAttachment}
          onRemovePending={onRemovePendingRemarkFile}
        />
      </div>
    </div>
  );
}

export default MidInspectionSheet;
